import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ContentEditor from './ContentEditor';
import ThumbnailPicker from './ThumbnailPicker';
import { getContentPreview, getContentStats, getFirstImageUrl } from '../utils/htmlUtils';

const NewsManager = ({ isOpen, onClose, initialMode = 'list', initialEditItem = null }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'published', 'draft'
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const [thumbnailPickerData, setThumbnailPickerData] = useState(null);

  // News categories
  const newsCategories = [
    { value: 'general', label: t('news.categories.general'), icon: '📰' },
    { value: 'performance', label: t('news.categories.performance'), icon: '🎭' },
    { value: 'rehearsal', label: t('news.categories.rehearsal'), icon: '🎵' },
    { value: 'event', label: t('news.categories.event'), icon: '📅' },
    { value: 'announcement', label: t('news.categories.announcement'), icon: '📢' },
    { value: 'social', label: t('news.categories.social'), icon: '👥' }
  ];

  // Load news items
  useEffect(() => {
    if (isOpen) {
      loadNewsItems();
    }
  }, [isOpen, filter]);

  // Handle initial mode when opening
  useEffect(() => {
    if (isOpen) {
      if (initialMode === 'create') {
        setEditingNews(null);
        setShowEditor(true);
      } else if (initialMode === 'edit' && initialEditItem) {
        setEditingNews(initialEditItem);
        setShowEditor(true);
      } else {
        // Default to list mode
        setEditingNews(null);
        setShowEditor(false);
      }
    } else {
      // Reset state when closing
      setEditingNews(null);
      setShowEditor(false);
    }
  }, [isOpen, initialMode, initialEditItem]);

  const loadNewsItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        operation: 'getContent',
        contentType: 'news',
        status: filter === 'all' ? '' : filter,
        limit: 50,
        userId: user.id
      });
      
      // Remove empty status param
      if (filter === 'all') {
        params.delete('status');
      }
      
      const response = await fetch(`${API_BASE}/api/content?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setNewsItems(data.content || []);
      } else {
        throw new Error('Failed to load news items');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNews = async (newsData) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    
    const response = await fetch(`${API_BASE}/api/content?operation=createContent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newsData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create news item');
    }
    
    // Reload the list
    await loadNewsItems();
  };

  const handleUpdateNews = async (newsData) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    
    const response = await fetch(`${API_BASE}/api/content?operation=updateContent&contentId=${newsData.contentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newsData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update news item');
    }
    
    // Reload the list
    await loadNewsItems();
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm(t('news.deleteConfirm'))) {
      return;
    }
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/content?operation=deleteContent&contentId=${newsId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userRole: user.role,
          userId: user.id
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete news item');
      }
      
      // Reload the list
      await loadNewsItems();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditNews = (newsItem) => {
    setEditingNews(newsItem);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingNews(null);
  };

  const handleThumbnailClick = (newsItem) => {
    setThumbnailPickerData(newsItem);
    setShowThumbnailPicker(true);
  };

  const handleThumbnailSelect = async (thumbnail) => {
    if (!thumbnailPickerData) return;
    
    // console.log('Selected thumbnail:', thumbnail);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      // Update the metadata with thumbnail info
      let currentMetadata = {};
      try {
        if (thumbnailPickerData.metadata && 
            typeof thumbnailPickerData.metadata === 'string' && 
            thumbnailPickerData.metadata !== '[object Object]' &&
            thumbnailPickerData.metadata !== 'null' &&
            thumbnailPickerData.metadata !== 'undefined') {
          currentMetadata = JSON.parse(thumbnailPickerData.metadata);
        } else if (typeof thumbnailPickerData.metadata === 'object' && thumbnailPickerData.metadata !== null) {
          currentMetadata = thumbnailPickerData.metadata;
        }
      } catch (error) {
        // console.warn('Invalid metadata found, starting fresh:', error);
        currentMetadata = {};
      }
      
      const updatedMetadata = {
        ...currentMetadata,
        customThumbnail: thumbnail
      };
      
      // console.log('Updating metadata:', updatedMetadata);
      
      const response = await fetch(`${API_BASE}/api/content?operation=updateContent&contentId=${thumbnailPickerData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metadata: updatedMetadata,
          userRole: user.role,
          userId: user.id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        throw new Error('Failed to update thumbnail');
      }
      
      const result = await response.json();
      // console.log('Update successful:', result);
      
      // Reload news items
      await loadNewsItems();
      setShowThumbnailPicker(false);
      setThumbnailPickerData(null);
    } catch (err) {
      console.error('Thumbnail update error:', err);
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return { bg: '#065f46', color: '#10b981', border: '#059669' };
      case 'draft': return { bg: '#451a03', color: '#f59e0b', border: '#d97706' };
      case 'archived': return { bg: '#374151', color: '#9ca3af', border: '#6b7280' };
      default: return { bg: '#374151', color: '#9ca3af', border: '#6b7280' };
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return newsCategories.find(cat => cat.value === categoryValue) || 
           { value: categoryValue, label: categoryValue, icon: '📰' };
  };

  const getCustomThumbnail = (item) => {
    try {
      if (!item?.metadata) return null;
      
      // Handle invalid metadata that might be "[object Object]" or other invalid JSON
      if (typeof item.metadata === 'string') {
        if (item.metadata === '[object Object]' || item.metadata === 'null' || item.metadata === 'undefined') {
          return null;
        }
        try {
          const metadata = JSON.parse(item.metadata);
          return metadata.customThumbnail || null;
        } catch {
          // If parsing fails, assume invalid metadata
          return null;
        }
      } else if (typeof item.metadata === 'object') {
        // Already an object
        return item.metadata.customThumbnail || null;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing metadata for item', item?.id, ':', error);
      return null;
    }
  };

  const renderThumbnail = (item, size = 48) => {
    const customThumbnail = getCustomThumbnail(item);
    const autoThumbnail = getFirstImageUrl(item.content_html || item.content_text);
    const stats = getContentStats(item.content_html || item.content_text);
    
    let thumbnailUrl = null;
    let fallbackIcon = stats.videoCount > 0 ? '🎬' : stats.imageCount > 0 ? '📷' : '📄';
    let backgroundColor = '#374151';
    
    if (customThumbnail) {
      if (customThumbnail.type === 'stock') {
        fallbackIcon = customThumbnail.icon.icon;
        backgroundColor = customThumbnail.icon.color + '20';
      } else if (customThumbnail.type === 'content') {
        thumbnailUrl = customThumbnail.url;
      } else if (customThumbnail.type === 'auto') {
        thumbnailUrl = autoThumbnail;
      }
    } else {
      thumbnailUrl = autoThumbnail;
    }
    
    return (
      <div style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '6px',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: '1px solid #4b5563',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleThumbnailClick(item);
      }}
      onMouseEnter={(e) => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.target.style.borderColor = '#4b5563';
        e.target.style.transform = 'scale(1)';
      }}
      title={t('thumbnailPicker.clickToChange')}
      >
        {thumbnailUrl ? (
          <img 
            src={thumbnailUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '5px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div style={{
          display: thumbnailUrl ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
          fontSize: `${size * 0.5}px` // Increased from 0.33 to 0.5
        }}>
          {fallbackIcon}
        </div>
        {/* Small edit indicator */}
        <div style={{
          position: 'absolute',
          bottom: '-2px',
          right: '-2px',
          width: '16px',
          height: '16px',
          backgroundColor: '#3b82f6',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '8px',
          color: 'white',
          border: '2px solid #1f2937'
        }}>
          ✏️
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      >
        {/* Modal Content */}
        <div 
          style={{
            backgroundColor: '#1f2937',
            borderRadius: '16px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid #374151'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)',
            color: '#ffffff',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #374151'
          }}>
            <div>
              <h2 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                📰 {t('news.management')}
              </h2>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                color: 'rgba(255,255,255,0.8)',
                fontWeight: '400'
              }}>
                {t('news.managementDescription')}
              </p>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                color: '#ffffff',
                fontSize: '24px',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }}
            >
              ✕
            </button>
          </div>

          {/* Controls Bar */}
          <div style={{
            padding: '20px 24px',
            backgroundColor: '#2d3748',
            borderBottom: '1px solid #374151',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Filter Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#e2e8f0'
              }}>
{t('news.filter')}
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  backgroundColor: '#374151',
                  color: '#e2e8f0',
                  fontSize: '14px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="all">{t('news.allItems')} ({newsItems.length})</option>
                <option value="published">{t('news.published')} ({newsItems.filter(item => item.status === 'published').length})</option>
                <option value="draft">{t('news.drafts')} ({newsItems.filter(item => item.status === 'draft').length})</option>
                <option value="archived">{t('news.archived')} ({newsItems.filter(item => item.status === 'archived').length})</option>
              </select>
            </div>

            {/* Create Button */}
            <button
              onClick={() => setShowEditor(true)}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '16px' }}>+</span>
{t('news.createNews')}
            </button>
          </div>

          {/* Content Area */}
          <div style={{
            padding: '0',
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
            backgroundColor: '#1f2937'
          }}>
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#9ca3af'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #374151',
                  borderTop: '4px solid #3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px'
                }} />
                <p style={{ margin: 0, fontSize: '16px' }}>{t('news.loadingNewsItems')}</p>
              </div>
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#f87171'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                <p style={{ margin: 0, fontSize: '16px' }}>{t('news.errorLoading')}: {error}</p>
                <button
                  onClick={loadNewsItems}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#374151',
                    color: '#e2e8f0',
                    border: '1px solid #4b5563',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
{t('news.tryAgain')}
                </button>
              </div>
            ) : newsItems.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>📰</div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#e2e8f0' }}>{t('news.noNewsFound')}</h3>
                <p style={{ margin: '0 0 24px 0', fontSize: '14px' }}>
                  {filter === 'all' ? t('news.createFirstNewsDescription') : t('news.noNewsFound')}
                </p>
                <button
                  onClick={() => setShowEditor(true)}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
{t('news.createFirstNews')}
                </button>
              </div>
            ) : (
              /* News Items Table */
              <div style={{ padding: '0' }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 100px 120px 160px 120px',
                  gap: '16px',
                  padding: '16px 24px',
                  backgroundColor: '#374151',
                  borderBottom: '1px solid #4b5563',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <div>{t('common.titleAndContent')}</div>
                  <div>{t('common.category')}</div>
                  <div>{t('common.status')}</div>
                  <div>{t('common.author')}</div>
                  <div>{t('common.created')}</div>
                  <div>{t('common.actions')}</div>
                </div>

                {/* Table Body */}
                {newsItems.map((item, index) => {
                  const statusStyle = getStatusColor(item.status);
                  const categoryInfo = getCategoryInfo(item.category);
                  
                  return (
                    <div
                      key={item.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 120px 100px 120px 160px 120px',
                        gap: '16px',
                        padding: '20px 24px',
                        borderBottom: index < newsItems.length - 1 ? '1px solid #374151' : 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2d3748';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Title & Content */}
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        {/* Thumbnail */}
                        {renderThumbnail(item)}
                        
                        {/* Text Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{
                            margin: '0 0 4px 0',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#f3f4f6',
                            lineHeight: '1.3'
                          }}>
{item.title || t('common.untitled')}
                          </h4>
                          <div style={{
                            margin: 0,
                            fontSize: '13px',
                            color: '#9ca3af',
                            lineHeight: '1.4'
                          }}>
                            <p style={{
                              margin: '0 0 4px 0',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {getContentPreview(item.content_html || item.content_text, 120)}
                            </p>
                            {(() => {
                              const stats = getContentStats(item.content_html || item.content_text);
                              return (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
{stats.wordCount} {t('news.words')}
                                  {stats.imageCount > 0 && (
                                    <span style={{ marginLeft: '8px' }}>
                                      📷 {stats.imageCount}
                                    </span>
                                  )}
                                  {stats.videoCount > 0 && (
                                    <span style={{ marginLeft: '8px' }}>
                                      🎬 {stats.videoCount}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Category */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '16px' }}>{categoryInfo.icon}</span>
                        <span style={{
                          fontSize: '12px',
                          color: '#d1d5db',
                          textTransform: 'capitalize'
                        }}>
                          {categoryInfo.label}
                        </span>
                      </div>

                      {/* Status */}
                      <div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: statusStyle.color,
                          backgroundColor: statusStyle.bg,
                          padding: '4px 8px',
                          borderRadius: '12px',
                          textTransform: 'uppercase',
                          border: `1px solid ${statusStyle.border}`,
                          letterSpacing: '0.5px'
                        }}>
                          {item.status}
                        </span>
                      </div>

                      {/* Author */}
                      <div style={{
                        fontSize: '13px',
                        color: '#d1d5db'
                      }}>
                        {item.author_name}
                      </div>

                      {/* Created */}
                      <div style={{
                        fontSize: '12px',
                        color: '#9ca3af'
                      }}>
                        {formatDate(item.created_at)}
                      </div>

                      {/* Actions */}
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditNews(item);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#374151',
                            color: '#d1d5db',
                            border: '1px solid #4b5563',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#4b5563';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#374151';
                          }}
                        >
{t('common.edit')}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNews(item.id);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#7f1d1d',
                            color: '#fca5a5',
                            border: '1px solid #991b1b',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#991b1b';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#7f1d1d';
                          }}
                        >
{t('common.delete')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Editor */}
      <ContentEditor
        isOpen={showEditor}
        onClose={handleCloseEditor}
        onSave={editingNews ? handleUpdateNews : handleCreateNews}
        contentType="news"
        editingContent={editingNews}
        categories={newsCategories}
        showAudioSupport={true}
        placeholder="Write your news content here..."
      />

      {/* Thumbnail Picker */}
      <ThumbnailPicker
        isOpen={showThumbnailPicker}
        onClose={() => {
          setShowThumbnailPicker(false);
          setThumbnailPickerData(null);
        }}
        onSelect={handleThumbnailSelect}
        contentHtml={thumbnailPickerData?.content_html || thumbnailPickerData?.content_text}
        currentThumbnail={getCustomThumbnail(thumbnailPickerData)}
      />
    </>
  );
};

export default NewsManager;